import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { API } from "@/lib/api";

// jsPDF default fonts (helvetica) don't support the Unicode ₹ glyph,
// which caused character-by-character spacing bugs and clipped values in the
// PDF. Use a plain "Rs." prefix with en-IN grouping to render cleanly.
const INR_GROUP = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const INR = { format: (n) => `Rs. ${INR_GROUP.format(Number(n) || 0)}` };

// ---- Asset caching ----
let logoDataUrl = null;
async function loadLogo() {
  if (logoDataUrl) return logoDataUrl;
  try {
    const res = await fetch("/bwz-logo.png");
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => { logoDataUrl = fr.result; resolve(fr.result); };
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

// Fetch a stored image via authed /api/files/{path} and return {dataUrl, w, h}
async function fetchImage(path) {
  try {
    const token = localStorage.getItem("bw_token");
    const res = await fetch(`${API}/files/${path}?auth=${encodeURIComponent(token)}`);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    const dim = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ w: 1, h: 1 });
      img.src = dataUrl;
    });
    return { dataUrl, ...dim };
  } catch { return null; }
}

const RED = [198, 40, 40];
const RED_DARK = [122, 15, 15];
const CREAM = [253, 250, 244];
const INK = [20, 20, 26];
const MUTED = [112, 112, 122];

export async function generateQuotePdf(quote) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // ===== HEADER — WHITE background for logo visibility =====
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, 120, "F");
  // thin red brand accent bar under the header
  doc.setFillColor(...RED);
  doc.rect(0, 118, W, 3, "F");

  const logo = await loadLogo();
  if (logo) {
    try { doc.addImage(logo, "PNG", 40, 28, 130, 44, undefined, "FAST"); }
    catch { /* ignore */ }
  }

  // Right-side top: company name and details
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text("BWZ Marcom Services Pvt Ltd", W - 40, 40, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  doc.text("www.brandonwheelz.com  ·  sales@brandonwheelz.com", W - 40, 56, { align: "right" });
  doc.text("Outdoor · Transit · Vehicle Media", W - 40, 68, { align: "right" });

  // QUOTATION label + Quote number pill
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...RED);
  doc.text("QUOTATION", 40, 100);

  doc.setFillColor(...RED);
  doc.roundedRect(W - 200, 82, 160, 24, 4, 4, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text(quote.quote_number || "—", W - 120, 98, { align: "center" });

  // ===== Meta block =====
  let y = 152;
  doc.setDrawColor(...MUTED);
  doc.setLineWidth(0.5);

  // BILL TO
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("BILL TO", 40, y);
  doc.setTextColor(...INK);
  doc.setFontSize(12);
  doc.text(quote.client?.name || "—", 40, y + 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const clientLines = [
    quote.client?.company,
    quote.client?.address,
    quote.client?.email,
    quote.client?.phone,
    quote.client?.gstin ? `GSTIN: ${quote.client.gstin}` : null,
  ].filter(Boolean);
  clientLines.forEach((line, i) => doc.text(String(line), 40, y + 32 + i * 12));

  // QUOTE DETAILS (right)
  const rx = W - 220;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("QUOTE DETAILS", rx, y);
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "normal");
  const details = [
    ["Date", new Date(quote.created_at || Date.now()).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })],
    ["Valid Until", quote.valid_until ? new Date(quote.valid_until).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"],
    ["Prepared By", quote.created_by_name || quote.created_by_email || "—"],
    ["Currency", quote.currency || "INR"],
  ];
  details.forEach(([k, v], i) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(k, rx, y + 16 + i * 14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text(String(v), rx + 80, y + 16 + i * 14);
  });

  // ===== Line items table =====
  const startY = y + 32 + Math.max(clientLines.length, 4) * 12 + 20;
  const head = [["#", "Service", "Cities", "Unit Price", "Qty", "Disc %", "Line Total"]];
  const body = (quote.line_items || []).map((li, i) => [
    i + 1,
    `${li.service_name}${li.duration ? `\n${li.duration}` : ""}${li.moq ? ` · ${li.moq}` : ""}`,
    Array.isArray(li.cities) ? li.cities.join(", ") : (li.cities || ""),
    INR.format(li.unit_price || 0),
    String(li.quantity || 0),
    `${li.discount_percent || 0}%`,
    INR.format(li.line_total || 0),
  ]);
  autoTable(doc, {
    head,
    body,
    startY,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 6, textColor: INK, lineColor: [230, 230, 230], overflow: "linebreak" },
    headStyles: { fillColor: RED, textColor: 255, fontStyle: "bold", halign: "left" },
    alternateRowStyles: { fillColor: CREAM },
    columnStyles: {
      0: { cellWidth: 22, halign: "center" },
      1: { cellWidth: 150 },
      2: { cellWidth: 90 },
      3: { halign: "right", cellWidth: 80 },
      4: { halign: "right", cellWidth: 32 },
      5: { halign: "right", cellWidth: 40 },
      6: { halign: "right", cellWidth: 100, fontStyle: "bold" },
    },
    margin: { left: 40, right: 40 },
  });

  // ===== Totals card =====
  const finalY = doc.lastAutoTable?.finalY || startY + 60;
  const boxX = W - 280;
  const boxY = finalY + 16;
  const rowH = 20;
  const rows = [
    ["Subtotal", INR.format(quote.subtotal || 0)],
    ["Discount", `- ${INR.format(quote.discount_total || 0)}`],
    ["Net Subtotal", INR.format(quote.net_subtotal || 0)],
    [`Tax (${quote.tax_percent || 0}%)`, INR.format(quote.tax_amount || 0)],
  ];
  doc.setDrawColor(230);
  doc.roundedRect(boxX, boxY, 240, rows.length * rowH + 40, 6, 6);
  doc.setFontSize(10);
  rows.forEach(([k, v], i) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(k, boxX + 14, boxY + 20 + i * rowH);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text(v, boxX + 226, boxY + 20 + i * rowH, { align: "right" });
  });
  const gtY = boxY + rows.length * rowH + 8;
  doc.setFillColor(...RED);
  doc.roundedRect(boxX, gtY, 240, 32, 6, 6, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text("GRAND TOTAL", boxX + 14, gtY + 20);
  doc.setFontSize(13);
  doc.text(INR.format(quote.total || 0), boxX + 226, gtY + 21, { align: "right" });

  // ===== Notes =====
  if (quote.notes) {
    const notesY = gtY + 60;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("NOTES", 40, notesY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...INK);
    const wrapped = doc.splitTextToSize(quote.notes, W - 320);
    doc.text(wrapped, 40, notesY + 14);
  }

  // ===== Sample References (images per line item) =====
  const withImages = (quote.line_items || []).filter((li) => Array.isArray(li.images) && li.images.length > 0);
  if (withImages.length > 0) {
    doc.addPage();
    let py = 60;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...RED);
    doc.text("SAMPLE REFERENCES", 40, py);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("Visual references for the services included in this quotation.", 40, py + 16);
    py += 40;

    // Prefetch all unique images in parallel
    const allPaths = [...new Set(withImages.flatMap((li) => li.images))];
    const imgMap = new Map();
    const fetched = await Promise.all(allPaths.map((p) => fetchImage(p)));
    allPaths.forEach((p, i) => imgMap.set(p, fetched[i]));

    for (const li of withImages) {
      // Page break if not enough room (need ~180pt for title + 2×2 images)
      if (py + 200 > H - 60) {
        doc.addPage();
        py = 60;
      }
      // Service header block
      doc.setFillColor(...CREAM);
      doc.rect(40, py, W - 80, 26, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...INK);
      doc.text(li.service_name, 50, py + 17);
      const meta = [li.category, Array.isArray(li.cities) ? li.cities.join(", ") : ""].filter(Boolean).join(" · ");
      if (meta) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...MUTED);
        doc.text(meta, W - 50, py + 17, { align: "right" });
      }
      py += 32;

      // 2×2 grid of thumbnails, 240x140 each with 10pt gap
      const gap = 10;
      const cellW = (W - 80 - gap) / 2;
      const cellH = 130;
      const startPy = py;
      li.images.slice(0, 4).forEach((path, idx) => {
        const col = idx % 2;
        const row = Math.floor(idx / 2);
        const x = 40 + col * (cellW + gap);
        const yy = startPy + row * (cellH + gap);
        // Background frame
        doc.setDrawColor(220);
        doc.setFillColor(250, 250, 250);
        doc.roundedRect(x, yy, cellW, cellH, 4, 4, "FD");
        const img = imgMap.get(path);
        if (img && img.dataUrl) {
          // Fit-contain the image inside the cell
          const scale = Math.min(cellW / img.w, cellH / img.h);
          const dw = img.w * scale;
          const dh = img.h * scale;
          const dx = x + (cellW - dw) / 2;
          const dy = yy + (cellH - dh) / 2;
          try {
            const fmt = path.toLowerCase().endsWith(".png") ? "PNG" : path.toLowerCase().endsWith(".webp") ? "WEBP" : "JPEG";
            doc.addImage(img.dataUrl, fmt, dx, dy, dw, dh, undefined, "FAST");
          } catch { /* ignore */ }
        } else {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(...MUTED);
          doc.text("Preview unavailable", x + cellW / 2, yy + cellH / 2, { align: "center" });
        }
      });
      const rowsUsed = li.images.length > 2 ? 2 : 1;
      py = startPy + rowsUsed * (cellH + gap) + 10;
    }
  }

  // ===== Footer on final page =====
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    const footerY = H - 60;
    doc.setDrawColor(220);
    doc.line(40, footerY - 12, W - 40, footerY - 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...RED);
    doc.text("Thank you for choosing BrandOnWheelz.", 40, footerY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(
      "Rates are inclusive of production unless specified. 50% advance to confirm booking. Prices valid till the date mentioned above.",
      40, footerY + 12, { maxWidth: W - 160 }
    );
    doc.text(`Page ${p}/${pageCount}`, W - 40, footerY, { align: "right" });
  }

  doc.save(`${quote.quote_number || "BWZ-Quote"}.pdf`);
}
